import axios from "axios";
import { API } from "./Api";

const MAX_TRACKED_ORDERS = 8;
const ACTIVE_ORDER_TTL_MS = 3 * 60 * 60 * 1000;
const COMPLETED_ORDER_TTL_MS = 20 * 60 * 1000;

const COMPLETED_STATUSES = new Set([
    "COMPLETED",
    "DELIVERED",
    "PICKED_UP",
    "DONE",
    "CLOSED",
    "CANCELLED",
    "CANCELED",
    "FAILED",
    "REJECTED",
]);

const READY_DISPLAY_STATUSES = new Set([
    "COMPLETED",
    "DELIVERED",
    "PICKED_UP",
    "DONE",
    "CLOSED",
]);

const CANCELLED_STATUSES = new Set([
    "CANCELLED",
    "CANCELED",
    "FAILED",
    "REJECTED",
]);

const storageKey = (outletId) =>
    `quickOrderTrackedOrders_${outletId || localStorage.getItem("outletId") || "default"}`;

const nowMs = () => Date.now();

export const isCompletedStatus = (status) =>
    COMPLETED_STATUSES.has(String(status || "").toUpperCase());

export const getTrackedOrderDisplayStatus = (entry) => {
    const status = String(entry?.status || "").trim().toUpperCase();

    if (CANCELLED_STATUSES.has(status)) {
        return {
            label: "Cancelled",
            key: "CANCELLED",
            isReady: false,
            isCancelled: true,
        };
    }

    if (READY_DISPLAY_STATUSES.has(status)) {
        return {
            label: "Ready",
            key: "COMPLETED",
            isReady: true,
            isCancelled: false,
        };
    }

    return {
        label: "Preparing",
        key: "PREPARING",
        isReady: false,
        isCancelled: false,
    };
};

const isExpired = (entry, now = nowMs()) => {
    const placedAt = new Date(entry?.placedAt).getTime();
    const completedAt = entry?.completedAt
        ? new Date(entry.completedAt).getTime()
        : null;

    if (Number.isFinite(completedAt)) {
        return now - completedAt > COMPLETED_ORDER_TTL_MS;
    }

    if (isCompletedStatus(entry?.status)) {
        const completedFallback = Number.isFinite(placedAt) ? placedAt : now;
        return now - completedFallback > COMPLETED_ORDER_TTL_MS;
    }

    if (!Number.isFinite(placedAt)) return true;

    return now - placedAt > ACTIVE_ORDER_TTL_MS;
};

const writeEntries = (outletId, entries) => {
    const key = storageKey(outletId);

    if (!entries.length) {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
        return;
    }

    localStorage.setItem(key, JSON.stringify(entries));
    sessionStorage.removeItem(key);
};

const readRawEntries = (outletId) => {
    const key = storageKey(outletId);

    try {
        const raw =
            localStorage.getItem(key) ||
            sessionStorage.getItem(key);

        if (!raw) return [];
        if (raw.startsWith("order_")) {
            return [
                {
                    razorpayOrderId: raw,
                    placedAt: new Date().toISOString(),
                    orderNumber: null,
                    status: null,
                    completedAt: null,
                },
            ];
        }

        const parsed = JSON.parse(raw);

        if (Array.isArray(parsed)) return parsed;

        const razorpayOrderId =
            parsed?.razorpayOrderId ||
            parsed?.order?.razorpayOrderId ||
            parsed?.order?.razorpay_order_id ||
            (typeof parsed === "string" ? parsed : null);

        if (!razorpayOrderId) return [];

        return [
            {
                razorpayOrderId,
                placedAt: parsed?.placedAt || new Date().toISOString(),
                orderNumber: parsed?.orderNumber || null,
                status: parsed?.status || null,
                completedAt: parsed?.completedAt || null,
            },
        ];
    } catch (error) {
        console.error("Invalid tracked orders:", error);
        return [];
    }
};

export const getTrackedOrders = (outletId) => {
    const fresh = readRawEntries(outletId).filter(
        (entry) => entry?.razorpayOrderId && !isExpired(entry),
    );

    writeEntries(outletId, fresh);
    return fresh;
};

export const addTrackedOrder = (
    outletId,
    razorpayOrderId,
    extras = {},
) => {
    if (!outletId || !razorpayOrderId) return;

    const existing = getTrackedOrders(outletId).filter(
        (entry) => entry.razorpayOrderId !== razorpayOrderId,
    );

    const next = [
        {
            razorpayOrderId,
            placedAt: extras.placedAt || new Date().toISOString(),
            orderNumber: extras.orderNumber || null,
            status: extras.status || "CONFIRMED",
            completedAt: extras.completedAt || null,
        },
        ...existing,
    ].slice(0, MAX_TRACKED_ORDERS);

    writeEntries(outletId, next);
};

export const updateTrackedOrder = (outletId, razorpayOrderId, patch = {}) => {
    if (!outletId || !razorpayOrderId) return;

    const next = getTrackedOrders(outletId).map((entry) => {
        if (entry.razorpayOrderId !== razorpayOrderId) return entry;

        const status = patch.status ?? entry.status;
        const completedAt =
            patch.completedAt !== undefined
                ? patch.completedAt
                : isCompletedStatus(status) && !entry.completedAt
                    ? new Date().toISOString()
                    : entry.completedAt;

        return {
            ...entry,
            ...patch,
            status,
            completedAt,
        };
    });

    writeEntries(outletId, next.filter((entry) => !isExpired(entry)));
};

export const removeTrackedOrder = (outletId, razorpayOrderId) => {
    if (!outletId || !razorpayOrderId) return;

    writeEntries(
        outletId,
        getTrackedOrders(outletId).filter(
            (entry) => entry.razorpayOrderId !== razorpayOrderId,
        ),
    );
};

export const getLastRazorpayOrderId = (outletId) =>
    getTrackedOrders(outletId)[0]?.razorpayOrderId || null;

export const saveLastRazorpayOrderId = (outletId, razorpayOrderId) => {
    addTrackedOrder(outletId, razorpayOrderId);
};

export const unwrapOrderPayload = (payload) => {
    if (!payload || typeof payload !== "object") {
        return null;
    }

    const order =
        payload.customerOrder ||
        payload.createdOrder ||
        payload.orderDetails ||
        payload.quickOrder ||
        payload.order ||
        payload;

    if (!order || typeof order !== "object") {
        return null;
    }

    const orderItems =
        payload.orderItems ||
        order.orderItems ||
        payload.items ||
        [];

    return {
        ...order,
        orderItems: Array.isArray(orderItems) ? orderItems : [],
    };
};

export const fetchQuickOrderByRazorpayId = async (razorpayOrderId) => {
    const response = await axios.post(
        `${API.BASE_URL}order/get-razorpay-order-quick-order`,
        { razorpayOrderId },
        {
            headers: {
                "Content-Type": "application/json",
            },
        },
    );

    return unwrapOrderPayload(response?.data?.data ?? response?.data);
};

export const getOrderNumber = (order) => {
    if (!order) return null;

    if (order.orderNumber) return String(order.orderNumber);
    if (order.order_no && order.order_sequence != null) {
        return `${order.order_sequence}${order.order_no}`;
    }
    if (order.order_no) return String(order.order_no);
    if (order.scanOrderId) return String(order.scanOrderId);
    if (order.tokenNumber) return String(order.tokenNumber);
    if (order.kotNumber) return String(order.kotNumber);

    return null;
};

export const isCreatedCustomerOrder = (payload) => {
    const order = unwrapOrderPayload(payload);

    if (!order || typeof order !== "object") {
        return false;
    }

    if (String(order.id || "").startsWith("order_") && !order._id) {
        return false;
    }

    if (getOrderNumber(order)) {
        return true;
    }

    if (
        order.orderStatus ||
        order.currentStatus ||
        order.kitchenStatus ||
        (Array.isArray(order.orderItems) && order.orderItems.length > 0) ||
        (Array.isArray(order.customerCartItemsData) &&
            order.customerCartItemsData.length > 0)
    ) {
        return true;
    }

    return /^[a-fA-F0-9]{24}$/.test(String(order._id || ""));
};

export const getOrderStatusKey = (order) =>
    String(
        order?.orderStatus ||
            order?.status ||
            order?.currentStatus ||
            order?.kitchenStatus ||
            "PLACED",
    )
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "_");

export const refreshTrackedOrderStatuses = async (outletId) => {
    const current = getTrackedOrders(outletId);

    const next = await Promise.all(
        current.map(async (entry) => {
            try {
                const order = await fetchQuickOrderByRazorpayId(
                    entry.razorpayOrderId,
                );

                if (!order) return entry;

                const status = getOrderStatusKey(order);

                return {
                    ...entry,
                    orderNumber: getOrderNumber(order) || entry.orderNumber,
                    status,
                    completedAt:
                        isCompletedStatus(status) && !entry.completedAt
                            ? new Date().toISOString()
                            : entry.completedAt,
                };
            } catch (error) {
                console.error(
                    "refreshTrackedOrderStatuses:",
                    error?.response?.data || error,
                );
                return entry;
            }
        }),
    );

    const fresh = next.filter((entry) => !isExpired(entry));
    writeEntries(outletId, fresh);
    return fresh;
};
